import React, { FC, useEffect, useContext, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import validate from "validate.js";
import axios, { AxiosError } from "axios";
import AppContext from "../../contexts/AppContext";
import Alert, { AlertType } from "../components/Alert";
import AuthLogo from "../components/AuthLogo";
import { useRecaptcha } from "../../hooks/useRecaptcha";
import { Agreement } from "./Agreement";
import RegisterContext from "./RegisterContext";
import { RegisterForm } from "./RegisterForm";
import { UseTranslate } from "../../hooks/Translate";

const RegisterPage: FC = () => {
  const { returnUrl, setReturnUrl } = useContext(AppContext);
  const { alertProps, setAlertProps } = useContext(RegisterContext);
  const [getRecaptchaToken] = useRecaptcha("userRegistration");
  const navigate = useNavigate();
  const [translate] = UseTranslate();

  const location = useLocation();
  const firstLoad = useRef(true);

  validate.validators.presence.options = {
    message: translate('cas.registration.requiredFields'),
  };


  const setParamData = useCallback(() => {
    const params = new URLSearchParams(location.search);
    if (firstLoad.current) {
      setReturnUrl(params.get("next"));
    }
    firstLoad.current = false;
  }, [setReturnUrl, location])

  useEffect(() => {
    setParamData();
  }, [setParamData]);

  const registerUser = async (data: any) => {
    try {
      const url = `${process.env.REACT_APP_CAS_API}/users/register`;
      const token = await getRecaptchaToken();

      const res = await axios.post(url, data, {
        headers: {
          token: token ? token.toString() : null
        }
      });

      if (res.data.status === "SUCCESS") {
        navigate(`/phone/${res.data.data._id}`);
      }
    } catch (e) {
      let message;
      if (e instanceof AxiosError && e.response) {
        message = translate(e.response.data.message)
      }
      setAlertProps({
        type: AlertType.ERROR,
        title: translate('cas.registration.registerError'),
        message
      });
    }
  };

  const goToLogin = () => {
    localStorage.removeItem('id_token');
    localStorage.removeItem('user_info');
    if (returnUrl) {
      window.location.replace(returnUrl);
    } else {
      navigate('/login');
    }
  };

  return (
    <>
      <div className="auth-wrapper">
        <div className="text-center mb-4">
          <AuthLogo />
          <h6>{translate('cas.registration.register')}</h6>
          <div className="text-muted small">
            {translate('cas.registration.fillOutForRegister')}
          </div>
        </div>
        <Alert
          type={alertProps!.type}
          title={alertProps!.title}
          message={alertProps!.message}
        />
        <RegisterForm register={registerUser} />
        <div className="mt-4 small">
          <span>{translate('cas.registration.haveAccount')}</span>
          <span onClick={goToLogin} className="link-button font-weight-bold ms-3">
            {translate('cas.registration.authorize')}
          </span>
        </div>
      </div>

      <Agreement />
    </>
  );
};

export default RegisterPage;
